import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { Pencil, Trash2, X, AlertCircle, Plus, ToggleLeft, ToggleRight, Check } from 'lucide-react';
import { PERMISSIONS } from '@pms/shared';
import { createSupportCategorySchema } from '@pms/shared';
import type { CreateSupportCategoryInput, UpdateSupportCategoryInput } from '@pms/shared';
import { techSupportService } from '@/services/tech-support.service';
import type { SupportCategory } from '@/services/tech-support.service';
import { usePermission } from '@/hooks/usePermission';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Tabs } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';

interface Props {
  onClose: () => void;
}

function formatMinutes(minutes: number): string {
  if (minutes >= 60) return `${Math.round(minutes / 60)}h`;
  return `${minutes}m`;
}

// ── Simple table primitives ──────────────────────────────────────────────────

function TableHead({ children }: { children: React.ReactNode }) {
  return (
    <thead>
      <tr className="bg-primary text-primary-foreground">
        {children}
      </tr>
    </thead>
  );
}

function Th({ children, className = '' }: { children?: React.ReactNode; className?: string }) {
  return (
    <th className={`px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider ${className}`}>
      {children}
    </th>
  );
}

function Td({ children, className = '' }: { children?: React.ReactNode; className?: string }) {
  return (
    <td className={`px-3 py-2.5 text-sm ${className}`}>
      {children}
    </td>
  );
}

// ── Categories Tab ───────────────────────────────────────────────────────────

function CategoriesTab() {
  const qc = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [pendingDeleteCat, setPendingDeleteCat] = useState<{ id: string; name: string } | null>(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['tech-support', 'categories', 'all'],
    queryFn: () => techSupportService.listAllCategories(),
  });

  const categories = data?.data?.data ?? [];

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['tech-support', 'categories'] });
  };

  const createMutation = useMutation({
    mutationFn: (input: CreateSupportCategoryInput) => techSupportService.createCategory(input),
    onSuccess: () => { toast.success('Category created'); reset(); invalidate(); },
    onError: () => toast.error('Failed to create category'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateSupportCategoryInput }) =>
      techSupportService.updateCategory(id, input),
    onSuccess: () => { toast.success('Category updated'); setEditingId(null); invalidate(); },
    onError: () => toast.error('Failed to update category'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => techSupportService.deleteCategory(id),
    onSuccess: () => { toast.success('Category deleted'); invalidate(); },
    onError: (err: { response?: { data?: { error?: { message?: string } } } }) =>
      toast.error(err?.response?.data?.error?.message ?? 'Failed to delete category'),
  });

  const { register, handleSubmit, reset, formState: { errors } } = useForm<CreateSupportCategoryInput>({
    resolver: zodResolver(createSupportCategorySchema),
  });

  const startEdit = (cat: SupportCategory) => {
    setEditingId(cat.id);
    setEditName(cat.name);
  };

  if (isLoading) {
    return (
      <div className="space-y-2 py-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-10 rounded bg-muted/50 animate-pulse" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center gap-2 py-8 text-muted-foreground">
        <AlertCircle className="w-5 h-5" />
        <p className="text-sm">Failed to load categories.</p>
        <Button variant="outline" size="sm" onClick={() => refetch()}>Retry</Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border/60 overflow-hidden">
        <table className="w-full">
          <TableHead>
            <Th>Name</Th>
            <Th>Status</Th>
            <Th />
          </TableHead>
          <tbody className="divide-y divide-border/60 bg-card">
            {categories.length === 0 && (
              <tr>
                <td colSpan={3} className="px-3 py-6 text-center text-sm text-muted-foreground italic">
                  No categories yet. Add one below.
                </td>
              </tr>
            )}
            {categories.map((cat) => (
              <tr key={cat.id} className="hover:bg-muted/30">
                <Td>
                  {editingId === cat.id ? (
                    <Input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="h-7 text-sm"
                      autoFocus
                    />
                  ) : (
                    <span className="font-medium text-foreground">{cat.name}</span>
                  )}
                </Td>
                <Td className="whitespace-nowrap">
                  <Badge variant={cat.is_active ? 'success' : 'secondary'}>
                    {cat.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </Td>
                <Td className="whitespace-nowrap text-right">
                  {editingId === cat.id ? (
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-10 w-10 px-0 text-primary hover:text-primary"
                        disabled={updateMutation.isPending}
                        onClick={() => updateMutation.mutate({ id: cat.id, input: { name: editName.trim() } })}
                        title="Save"
                      >
                        <Check className="w-5 h-5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-10 w-10 px-0 text-muted-foreground"
                        onClick={() => setEditingId(null)}
                        title="Cancel"
                      >
                        <X className="w-5 h-5" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-10 w-10 px-0 text-muted-foreground hover:text-foreground"
                        onClick={() => startEdit(cat)}
                        title="Edit name"
                      >
                        <Pencil className="w-5 h-5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className={`h-10 w-10 px-0 ${cat.is_active ? 'text-success hover:text-muted-foreground' : 'text-muted-foreground hover:text-success'}`}
                        disabled={updateMutation.isPending}
                        onClick={() => updateMutation.mutate({ id: cat.id, input: { is_active: !cat.is_active } })}
                        title={cat.is_active ? 'Disable' : 'Enable'}
                      >
                        {cat.is_active
                          ? <ToggleRight className="w-5 h-5" />
                          : <ToggleLeft className="w-5 h-5" />}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-10 w-10 px-0 text-muted-foreground hover:text-destructive"
                        disabled={deleteMutation.isPending}
                        onClick={() => setPendingDeleteCat({ id: cat.id, name: cat.name })}
                        title="Delete"
                      >
                        <Trash2 className="w-5 h-5" />
                      </Button>
                    </div>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <form
        onSubmit={handleSubmit((vals) => createMutation.mutate(vals))}
        className="border-t border-border/60 pt-4"
      >
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
          New Category
        </p>
        <div className="flex gap-2 items-end">
          <div className="flex-1 space-y-1">
            <Label>Category Name</Label>
            <Input {...register('name')} placeholder="e.g. Hardware Issue" className="h-10" />
            {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
          </div>
          <Button type="submit" className="h-10 px-4 shrink-0" disabled={createMutation.isPending}>
            <Plus className="w-4 h-4 mr-1.5" />
            {createMutation.isPending ? 'Adding…' : 'Add Category'}
          </Button>
        </div>
      </form>

      <ConfirmDialog
        open={!!pendingDeleteCat}
        onOpenChange={(o) => { if (!o) setPendingDeleteCat(null); }}
        title={`Delete "${pendingDeleteCat?.name}"?`}
        description="This cannot be undone."
        confirmLabel="Delete"
        onConfirm={() => { if (pendingDeleteCat) deleteMutation.mutate(pendingDeleteCat.id); }}
      />
    </div>
  );
}

// ── SLA Tab ──────────────────────────────────────────────────────────────────

const PRIORITY_ORDER = ['low', 'medium', 'high', 'urgent'] as const;
const PRIORITY_VARIANT: Record<string, 'outline' | 'secondary' | 'warning' | 'destructive'> = {
  low: 'outline', medium: 'secondary', high: 'warning', urgent: 'destructive',
};

function SlaTab() {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['tech-support', 'sla-policies'],
    queryFn: () => techSupportService.listSlaPolicies(),
  });

  const raw = data?.data?.data ?? [];
  const policies = [...raw].sort(
    (a, b) => PRIORITY_ORDER.indexOf(a.priority) - PRIORITY_ORDER.indexOf(b.priority),
  );

  if (isLoading) {
    return (
      <div className="space-y-2 py-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-10 rounded bg-muted/50 animate-pulse" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center gap-2 py-8 text-muted-foreground">
        <AlertCircle className="w-5 h-5" />
        <p className="text-sm">Failed to load SLA policies.</p>
        <Button variant="outline" size="sm" onClick={() => refetch()}>Retry</Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border/60 overflow-hidden">
        <table className="w-full">
          <TableHead>
            <Th>Priority</Th>
            <Th>Policy Name</Th>
            <Th className="whitespace-nowrap">First Response</Th>
            <Th className="whitespace-nowrap">Resolution</Th>
          </TableHead>
          <tbody className="divide-y divide-border/60 bg-card">
            {policies.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-6 text-center text-sm text-muted-foreground italic">
                  No SLA policies configured.
                </td>
              </tr>
            )}
            {policies.map((p) => (
              <tr key={p.id} className="hover:bg-muted/30">
                <Td className="whitespace-nowrap">
                  <Badge variant={PRIORITY_VARIANT[p.priority] ?? 'secondary'} className="capitalize">
                    {p.priority}
                  </Badge>
                </Td>
                <Td className="text-foreground">{p.name}</Td>
                <Td className="font-mono whitespace-nowrap text-muted-foreground">{formatMinutes(p.response_minutes)}</Td>
                <Td className="font-mono whitespace-nowrap text-muted-foreground">{formatMinutes(p.resolution_minutes)}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">
        SLA policies are tied to ticket priority. Contact your database administrator to modify times.
      </p>
    </div>
  );
}

// ── Main Dialog ──────────────────────────────────────────────────────────────

export function TechSupportSettingsDialog({ onClose }: Props) {
  const { has } = usePermission();
  const canManageCategories = has(PERMISSIONS.TECH_SUPPORT_MANAGE_CATEGORIES);
  const canManageSla = has(PERMISSIONS.TECH_SUPPORT_MANAGE_SLA);

  const allTabs = [
    { key: 'categories', label: 'Categories', show: canManageCategories },
    { key: 'sla', label: 'SLA Policies', show: canManageSla },
  ];
  const visibleTabs = allTabs.filter((t) => t.show).map(({ key, label }) => ({ key, label }));
  const [tab, setTab] = useState(visibleTabs[0]?.key ?? 'categories');

  if (visibleTabs.length === 0) return null;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Tech Support Settings</DialogTitle>
        </DialogHeader>

        {visibleTabs.length > 1 && (
          <Tabs tabs={visibleTabs} active={tab} onChange={setTab} />
        )}

        <div className="max-h-[60vh] overflow-y-auto">
          {tab === 'categories' && canManageCategories && <CategoriesTab />}
          {tab === 'sla' && canManageSla && <SlaTab />}
        </div>
      </DialogContent>
    </Dialog>
  );
}
